import { Box } from "@chakra-ui/react";
import { useAppSelector } from "hooks/store/hooks";
import React, { Suspense, useEffect, useMemo, useState } from "react";
import lazy from "utils/lazyWithRetry";
import { Route, Routes } from "react-router-dom";
import {
  selectAppDisable,
  selectCurrentDevice,
  selectpaypalClientId,
} from "stores/auth/authSlice";
import { getUserDetails } from "utils/local-storage";

const Index = lazy(() => import("pages/index"));
const EulaPage = lazy(() => import ("pages/EulaPage"));
const ActivationPage = lazy(() => import("pages/ActivationPage"));
const VerificationPage = lazy(() => import("pages/VerificationPage"));
const LoginWithOTPPage = lazy(() => import("pages/LoginWithOTP"));
const DashboardPage = lazy(() => import("pages/DashboardPage/index"));
const ForgotPasswordPage = lazy(() => import("pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("pages/ResetPasswordPage"));
const QuickAccessBarPage = lazy(() =>
  import("pages/QuickAccessBarViewComponentPage/index")
);
const TimelinePage = lazy(() => import("pages/TimelinePage"));
const LivePanel = lazy(() => import("pages/LivePanel"));
const FacebookPage = lazy(() => import("pages/FacebookPage"));
const DirectoryPage = lazy(() => import("pages/DirectoryPage"));
const KeyLoggerPage = lazy(() => import("pages/KeyLoggerPage"));
const ClipboardPage = lazy(() => import("pages/ClipboardPage"));
const PhotosPage = lazy(() => import("pages/PhotosPage"));
const WebHistoryPage = lazy(() => import("pages/WebHistoryPage"));
const ContactsPage = lazy(() => import("pages/ContactsPage"));
const RemoteCommands = lazy(() =>
  import("pages/RemoteCommands/RemoteCommands")
);
const AntiTheftPage = lazy(() => import("pages/AntiTheftPage"));
const BlockSitePage = lazy(() => import("pages/BlockSitePage"));
const MonitoringwebsitePage = lazy(() => import("pages/MonitoringwebsitePage"));
const ScreenTimePage = lazy(() => import("pages/ScreenTimePage"));
const WebFilterPage = lazy(() => import("pages/WebFilterPage"));
const WifiHistoryPage = lazy(() => import("pages/WifiHistoryPage"));
const NotificationsPage = lazy(() => import("pages/NotificationsPage"));
const SchedulePage = lazy(() => import("pages/SchedulePage"));
const SDCardPage = lazy(() => import("pages/SDCardPage"));
const LocationHistory = lazy(() => import("pages/AllLocationHistoryPage"));
const AppsPage = lazy(() => import("pages/AppsPage"));
const InstantIMPage = lazy(() => import("pages/InstantIMPage"));
const AppUsagePage = lazy(() => import("pages/AppUsagePage"));
const WhatsappWebPage = lazy(() => import("pages/WhatsappWebPage"));
const AppsRecordingPage = lazy(() => import("pages/AppsRecordingPage"));
const TextCommandPage = lazy(() => import("pages/TextCommandPage"));
const HealthPage = lazy(() => import("pages/HealthPage"));
const SubscriptionPage = lazy(() => import("pages/SubscriptionPage"));
const TransferPage = lazy(() => import("pages/TransferPage"));
const ChangePassword = lazy(() => import("pages/ChangePassword"));
const DataManagementPage = lazy(() => import("pages/DataManagementPage"));
const SystemLogEventsPage = lazy(() => import("pages/SystemLogEventsPage"));
const SettingsPage = lazy(() => import("pages/SettingsPage"));
const RegisterNewDevice = lazy(() => import("pages/RegisterNewDevice"));
const PaymentPage = lazy(() => import("pages/PaymentPage"));
const PaymentSuccessPage = lazy(() => import("pages/PaymentSuccessPage"));
const WhatsappWebChatPage = lazy(() => import("pages/WhatsappWebChatPage"));
const ReInstallQRCodePage = lazy(() => import("pages/ReInstallQRCodePage"));
const SMS = lazy(() => import("pages/SMSpage"));
const Calls = lazy(() => import("pages/Callslog"));

const Router = () => {
  // Select values from the Redux store
  const { currentDevice, clientId, appDisable } = useAppSelector((state) => ({
    currentDevice: selectCurrentDevice(state),
    clientId: selectpaypalClientId(state),
    appDisable: selectAppDisable(state),
  }));

  const userDetails = useMemo(() => getUserDetails(), []);

  // State variables
  const [subscriptionFinish, setSubscriptionFinish] = useState(false);
  const [registerNewDevicePage, setRegisterNewDevicePage] = useState(false);
  const [userVerified, setUserVerified] = useState(true);

  // Effect to handle user email verification and device registration
  useEffect(() => {
    const emailVerified = userDetails?.email_status === "1";
    setUserVerified(emailVerified);
    setRegisterNewDevicePage(emailVerified && userDetails?.devices?.length === 0);
  }, [userDetails]);

  // Effect to update subscription status based on appDisable flag
  useEffect(() => {
    if (appDisable !== undefined) {
      setSubscriptionFinish(appDisable);
    }
  }, [appDisable]);

  // PayPal configuration options
  const initialOptions = useMemo(
    () => ({
      "client-id": clientId,
      currency: "USD",
      intent: "capture",
    }),
    [clientId]
  );

  // Function to get the protected component
  const getProtectedComponent = (Component) => {
    if (!userVerified) {
      return <VerificationPage currentDevice={currentDevice} />;
    }
    if (registerNewDevicePage) {
      return <RegisterNewDevice />;
    }
    if (subscriptionFinish) {
      return <SubscriptionPage currentDevice={currentDevice} />;
    }
    return Component;
  };

  return (
    <Suspense
      fallback={
        <Box textAlign="center" marginY="100px">
          {/* <Spinner /> */}
        </Box>
      }
    >
      <Routes>
        {/* Basic Routes */}
        <Route path="/LoginWithOtp" element={<LoginWithOTPPage />} />
        <Route path="/" element={<Index />} />
        <Route path="/registerNewDevice" element={<RegisterNewDevice />} />

        {/* Activation Routes */}
        <Route
          path="/activation"
          element={<ActivationPage currentDevice={currentDevice} />}
        />
        <Route
          path="/activation/:code"
          element={<EulaPage />}
        />
        <Route
          path="/eula"
          element={<EulaPage />}
        />

        {/* Authentication Routes */}
        <Route
          path="/verification"
          element={<VerificationPage currentDevice={currentDevice} />}
        />
        <Route
          path="/forgotPassword"
          element={<ForgotPasswordPage currentDevice={currentDevice} />}
        />
        <Route
          path="/resetPassword/:code"
          element={<ResetPasswordPage currentDevice={currentDevice} />}
        />
        <Route
          path="/resetPassword"
          element={<ResetPasswordPage currentDevice={currentDevice} />}
        />

        {/* Routes with Conditional Rendering */}
        {[
          { path: "/quickAccessBar", component: <QuickAccessBarPage /> },
          {
            path: "/dashboard",
            component: <DashboardPage currentDevice={currentDevice} />,
          },
          {
            path: "/Calls",
            component: <Calls currentDevice={currentDevice} />,
          },
          {
            path: "/SMS",
            component: <SMS currentDevice={currentDevice} />,
          },
          {
            path: "/timeline",
            component: <TimelinePage currentDevice={currentDevice} />,
          },
          { path: "/livepanel", component: <LivePanel /> },
          {
            path: "/facebook",
            component: <FacebookPage currentDevice={currentDevice} />,
          },
          {
            path: "/directory",
            component: <DirectoryPage currentDevice={currentDevice} />,
          },
          {
            path: "/keylogger",
            component: <KeyLoggerPage currentDevice={currentDevice} />,
          },
          {
            path: "/clipboard",
            component: <ClipboardPage currentDevice={currentDevice} />,
          },
          {
            path: "/photos",
            component: <PhotosPage currentDevice={currentDevice} />,
          },
          {
            path: "/webhistory",
            component: <WebHistoryPage currentDevice={currentDevice} />,
          },
          {
            path: "/contacts",
            component: <ContactsPage currentDevice={currentDevice} />,
          },
          {
            path: "/remoteCommands",
            component: <RemoteCommands currentDevice={currentDevice} />,
          },
          {
            path: "/antiTheft",
            component: <AntiTheftPage currentDevice={currentDevice} />,
          },
          {
            path: "/blockSite",
            component: <BlockSitePage currentDevice={currentDevice} />,
          },
          {
            path: "/monitoringwebsite",
            component: <MonitoringwebsitePage currentDevice={currentDevice} />,
          },
          {
            path: "/screenTime",
            component: <ScreenTimePage currentDevice={currentDevice} />,
          },
          {
            path: "/webFilter",
            component: <WebFilterPage currentDevice={currentDevice} />,
          },
          {
            path: "/wifiHistory",
            component: <WifiHistoryPage currentDevice={currentDevice} />,
          },
          {
            path: "/whatsappWebChat",
            component: <WhatsappWebChatPage currentDevice={currentDevice} />,
          },
          {
            path: "/reInstallQRCode",
            component: <ReInstallQRCodePage currentDevice={currentDevice} />,
          },
          {
            path: "/notifications",
            component: <NotificationsPage currentDevice={currentDevice} />,
          },
          {
            path: "/schedule",
            component: <SchedulePage currentDevice={currentDevice} />,
          },
          {
            path: "/sdCard",
            component: <SDCardPage currentDevice={currentDevice} />,
          },
          {
            path: "/location",
            component: <LocationHistory currentDevice={currentDevice} />,
          },
          {
            path: "/apps",
            component: <AppsPage currentDevice={currentDevice} />,
          },
          {
            path: "/instantIM",
            component: <InstantIMPage currentDevice={currentDevice} />,
          },
          {
            path: "/appUsage",
            component: <AppUsagePage currentDevice={currentDevice} />,
          },
          {
            path: "/whatsappWeb",
            component: <WhatsappWebPage currentDevice={currentDevice} />,
          },
          {
            path: "/appsRecording",
            component: <AppsRecordingPage currentDevice={currentDevice} />,
          },
          {
            path: "/textCommand",
            component: <TextCommandPage currentDevice={currentDevice} />,
          },
          {
            path: "/health",
            component: <HealthPage currentDevice={currentDevice} />,
          },
          {
            path: "/settings",
            component: <SettingsPage currentDevice={currentDevice} />,
          },
          {
            path: "/transfer",
            component: <TransferPage currentDevice={currentDevice} />,
          },
          {
            path: "/changePassword",
            component: <ChangePassword currentDevice={currentDevice} />,
          },
          {
            path: "/dataManagement",
            component: <DataManagementPage currentDevice={currentDevice} />,
          },
          {
            path: "/systemLogEvents",
            component: <SystemLogEventsPage currentDevice={currentDevice} />,
          },
          {
            path: "/subscription",
            component: <SubscriptionPage currentDevice={currentDevice} />,
          },
          {
            path: "/payment",
            component: <PaymentPage currentDevice={currentDevice} />,
          },
          {
            path: "/paymentSuccess",
            component: <PaymentSuccessPage currentDevice={currentDevice} />,
          },

        ].map(({ path, component }) => (
          <Route
            key={path}
            path={path}
            element={getProtectedComponent(component)}
          />
        ))}
        {/* <PayPalScriptProvider options={initialOptions}> */}
        <Route
          path="/payment/:paymentType/:id"
          element={
            <PaymentPage currentDevice={currentDevice} />
          } />
        {/* </PayPalScriptProvider> */}
      </Routes>
    </Suspense>
  );
};

export default Router;
